import {Component, ElementRef, OnInit, ViewChild} from '@angular/core';
import {MatSort, Sort} from '@angular/material/sort';
import {MatPaginator, PageEvent} from '@angular/material/paginator';
import {FormControl} from '@angular/forms';
import {MatDialog} from '@angular/material/dialog';
import {ActivatedRoute, Params} from '@angular/router';
import {TableControl} from '../../../../../common/models/TableControl';
import {DateUtil} from '../../../../../common/util/DateUtil';
import {PagedRequestDTO} from '../../../../../common/models/PagedRequestDTO';
import {DisplayIcon} from 'src/app/modules/common/components';
import {
  ConfirmationDialogComponent
} from '../../../../../common/dialogs/confirmation-dialog/confirmation-dialog.component';
import {detailsExpand} from '../../../../../common';
import {AtcDTO} from '../../../../models/AtcDTO';
import {AtcService} from '../../../../services/atc.service';
import {AddAtcDialogComponent} from '../../../dialogs/add-atc-dialog/add-atc-dialog.component';
import {createFieldMap, getSortDirection} from '../../../../../common/util/Util';
import {SharedService} from '../../../../../common/services/shared-service';
import {EntityTable} from '../../../../../common/types/entity-table.enum';

@Component({
  selector: 'app-atc-codes',
  templateUrl: './atc-codes.component.html',
  styleUrls: [
    '../../../../../../../styles/global/_management-styles.scss'
  ],
  animations: [detailsExpand]
})
export class AtcCodesComponent implements OnInit {
  readonly DisplayIcon = DisplayIcon;

  atcFormControl: FormControl;
  atcExpandedRow: AtcDTO | null;
  atcTableControl: TableControl;
  atcDisplayedColumns: string[] = [
    'icons',
    'atcId',
    'code',
    'name',
    'level',
    'source'
  ];
  atcDataSource: AtcDTO[];

  isConfirmed = false;
  dateFormat = DateUtil.dateFormat;
  dateTimeFormat = DateUtil.dateTimeFormat;
  length = 100;
  pageSize = 10;
  pageNumber = 0;
  pageSizeOptions: number[] = [5, 10, 25, 100];

  @ViewChild('atcSort', {static: false}) atcSort: MatSort;
  @ViewChild('matSort', {static: false}) tableSort: MatSort;
  @ViewChild('atcPaginator', {static: false}) atcPaginator: MatPaginator;
  @ViewChild('atcCodeSearch') private atcCodeSearch: ElementRef;

  constructor(
    private atcService: AtcService,
    private activatedRoute: ActivatedRoute,
    public dialog: MatDialog,
    private sharedService: SharedService,
  ) {
    this.atcFormControl = new FormControl('');

    this.atcTableControl = new TableControl(
      this.length,
      this.pageSize,
      this.pageNumber,
      this.pageSizeOptions,
      'atcId',
      'desc',
      'name',
      ''
    );
  }

  ngOnInit(): void {
    this.initSubscriptions();
    this.getListOfAtcCodes(this.atcTableControl);
  }

  initSubscriptions() {
    this.activatedRoute
      .queryParams
      .subscribe((params: Params) => {
        if (params.tab === 'atc-codes' && params.dialog) {
          this.openAtcDialog();
        }
      });
  }

  getListOfAtcCodes(tableControl: TableControl): void {
    const pagedRequestDTO = new PagedRequestDTO();
    pagedRequestDTO.entityClass = EntityTable.TblAtc;
    pagedRequestDTO.pageNumber = tableControl.pageNumber;
    pagedRequestDTO.pageSize = tableControl.pageSize;
    pagedRequestDTO.sortField = tableControl.sortField;
    pagedRequestDTO.sortDirection = getSortDirection(tableControl.sortDirection);
    pagedRequestDTO.fieldMap = createFieldMap(tableControl.filterField, tableControl.filterValue);

    this.atcService.getListOfAtcCodes(pagedRequestDTO).subscribe(
      response => {
        this.atcDataSource = response.resultList;
        this.atcTableControl.length = response.totalRecords;
      },
      errorResponse => {
        const error = errorResponse.error;
        this.sharedService.openErrorDialog(error.businessMessage);
      }
    );
  }

  pageChanged($event: PageEvent) {
    this.atcTableControl.pageNumber = $event.pageIndex;
    this.atcTableControl.pageSize = $event.pageSize;
    this.getListOfAtcCodes(this.atcTableControl);
  }

  sortData($event: Sort) {
    if (this.atcPaginator != null) {
      this.atcPaginator.firstPage();
    }
    this.atcTableControl.pageNumber = 0;
    this.atcTableControl.sortField = $event.active;
    this.atcTableControl.sortDirection = $event.direction;
    this.getListOfAtcCodes(this.atcTableControl);
  }

  onInputChangedFilterData(filterField: string) {
    if (this.atcPaginator != null) {
      this.atcPaginator.firstPage();
    }
    this.atcTableControl.pageNumber = 0;
    this.atcTableControl.filterField = filterField;
    this.atcTableControl.filterValue = this.atcFormControl.value;
    this.getListOfAtcCodes(this.atcTableControl);
  }

  deleteEntry(event: MouseEvent, id: number) {
    event.preventDefault();
    event.stopPropagation();

    const dialogRef = this.dialog.open(ConfirmationDialogComponent, {
      width: '40vw',
      data: 'Are you sure you wish to delete this entity?'
    });

    dialogRef.afterClosed().subscribe((result) => {
      const isConfirmed = result;

      if (isConfirmed) {
        this.atcService.deleteAtcCode(id).subscribe(
          () => {
            this.sharedService.openSnackBar(`ATC ${id} deleted`, 'Dismiss');
            this.getListOfAtcCodes(this.atcTableControl);
          },
          (errorResponse) => {
            const error = errorResponse.error;
            this.sharedService.openErrorDialog(error.businessMessage);
          }
        );
      }
    });
  }

  editEntry(event: MouseEvent, id: number) {
    event.preventDefault();
    event.stopPropagation();

    this.openAtcDialog(id);
  }

  openAtcDialog(id?: number) {
    const dialogRef = this.dialog.open(AddAtcDialogComponent, {
      width: '40vw',
      data: id
    });

    dialogRef.afterClosed().subscribe(
      () => {
        this.getListOfAtcCodes(this.atcTableControl);
      },
      (error) => {
        this.sharedService.openErrorDialog(error.message);
      }
    );
  }
}
